import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { EAssetType, EChannel, IAsset } from 'src/app/models/products/asset';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { ProductsService } from 'src/app/services/products.service';
import { IBoundingBox } from 'src/app/models/boundingBox';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { get } from 'lodash';

@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ChatBoxComponent implements OnInit, AfterViewInit {
  @Input() currentAsset!: IAsset;
  @Input() assetDetail!: IAsset;
  @Input() onlyProperties = false;
  @Input() isCurrent = false;
  @Input() productTouchAssetId = '';
  @Input() masterSuiteTouchAssetId = '';
  @Input() organizationId = '';
  @Input() commentBoundingBox: IBoundingBox | null = null;
  @Input() contentExtracting = false;
  @Input() assetType = '';
  @Output() versionSelected = new EventEmitter();
  @Output() commentHover = new EventEmitter();
  @Output() clearBoundingBox = new EventEmitter();
  @Output() propertiesSelected = new EventEmitter<boolean>();
  @Output() labelViewEmitter = new EventEmitter<boolean>();
  @Output() boundingBoxProportionEmitter = new EventEmitter<IBoundingBox | null>();
  @Output() contentExtractingEmitter = new EventEmitter<boolean>();

  Permission = Permission;
  listChannel: { value: EChannel; label: string }[] = [
    { value: EChannel.EXTERNAL, label: 'DISCUSSION' },
  ];
  currentChannel = EChannel.EXTERNAL;
  isFirstLoaded = false;
  propertiesView = false;
  labelView = false;

  get selectedChannelIndex(): number {
    return this.listChannel.findIndex(c => c.value === this.currentChannel);
  }

  get hasCommentPermission(): boolean {
    return (this.assetType === 'shoot-asset' && this.authService.hasPermission(Permission.ShootAssetsComment))
      || (this.assetType === 'post-asset' && (this.authService.hasPermission(Permission.PostAssetsInternalComment) || this.authService.hasPermission(Permission.PostAssetsExternalComment)));
  }

  constructor(
    public authService: AuthService,
    private route: ActivatedRoute,
    private productService: ProductsService,
  ) {}

  ngOnInit(): void {
    if (this.onlyProperties) {
      this.isFirstLoaded = true;
      this.propertiesView = true;
      this.propertiesSelected.emit(true);
    } else {
      this.initChannel();
    }
    const section = this.route.snapshot.queryParamMap.get('section');
    if (section === 'labels') {
      this.propertiesView = false;
      this.labelView = true;
      this.labelViewEmitter.emit(true);
    }
  }

  ngAfterViewInit(): void {
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      this.listChannel = [{ value: EChannel.INTERNAL, label: 'DISCUSSION' }];
    } else {
      const channelConfig = [
        { value: EChannel.INTERNAL, label: 'INTERNAL DISCUSSION', permission: Permission.PostAssetsInternalComment },
        { value: EChannel.EXTERNAL, label: 'EXTERNAL DISCUSSION', permission: Permission.PostAssetsExternalComment },
      ];
      const channels = channelConfig
        .filter(c => this.authService.hasPermission(c.permission))
        .map(({ value, label }) => ({ value, label }));
      if (channels.length > 0) {
        this.listChannel = channels;
        this.currentChannel = channels[0].value;
      }
    }
  }

  onChangeChannel(tabChangeEvent: MatTabChangeEvent): void {
    if (tabChangeEvent.tab.textLabel === 'PROPERTIES') {
      this.propertiesView = true;
      this.labelView = false;
      this.propertiesSelected.emit(true);
      this.labelViewEmitter.emit(false);
    } else if (tabChangeEvent.tab.textLabel === 'LABEL') {
      this.propertiesView = false;
      this.labelView = true;
      this.propertiesSelected.emit(false);
      this.labelViewEmitter.emit(true);
    } else {
      this.propertiesView = false;
      this.labelView = false;
      this.labelViewEmitter.emit(false);
      this.propertiesSelected.emit(false);
      this.currentChannel = this.listChannel[tabChangeEvent.index]?.value ?? EChannel.EXTERNAL;
      this.productService.setPostAssetState(this.currentChannel);
    }
  }

  private initChannel(): void {
    if (this.assetType === EAssetType.SHOOT_ASSETS) {
      this.currentChannel = EChannel.INTERNAL;
      this.isFirstLoaded = true;
    } else {
      const defaultChannel = this.resolveDefaultChannel();
      this.productService.setPostAssetState(defaultChannel);
      this.productService.postAssetState.subscribe({
        next: (data: EChannel) => {
          this.currentChannel = data;
          this.isFirstLoaded = true;
        },
      });
    }
  }

  private resolveDefaultChannel(): EChannel {
    const channel = get(this.route, 'snapshot.queryParams.channel', '');
    if (channel === 'external' && this.authService.hasPermission(Permission.PostAssetsExternalComment)) {
      return EChannel.EXTERNAL;
    }
    if (this.authService.hasPermission(Permission.PostAssetsInternalComment)) {
      return EChannel.INTERNAL;
    }
    return EChannel.EXTERNAL;
  }
}
